import React from 'react';
import { X, File, FileText, FileSpreadsheet, Download, Paperclip } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import AppliedDiscountBanner from './AppliedDiscountBanner';
import EarlyPaymentBanner from './EarlyPaymentBanner';
import PlanBadge from '../common/PlanBadge';
import { buildContactFromSources, normalizeContactDetails } from '../../utils/contactNormalization';

const InvoicePreview = ({ 
  invoice, 
  isOpen, 
  onClose, 
  onDownloadPDF, 
  onDownloadExcel,
  showDownloadButtons = true,
  title = "Invoice Preview"
}) => {
  const { user } = useAuth();
  
  if (!isOpen || !invoice) return null;

  const safeSender = buildContactFromSources(
    invoice.sender,
    invoice.supplier,
    invoice.from,
    invoice.metadata?.sender,
    invoice.metadata?.senderSnapshot,
    invoice.senderSnapshot,
    invoice.senderDetails
  );

  const safeReceiver = (() => {
    if (!invoice.receiverId && invoice.guestRecipient) {
      return normalizeContactDetails(invoice.guestRecipient);
    }
    return buildContactFromSources(
      invoice.receiver,
      invoice.customer,
      invoice.metadata?.receiver,
      invoice.metadata?.receiverSnapshot,
      invoice.receiverSnapshot,
      invoice.recipient,
      invoice.company
    );
  })();

  const normalizedUserId = user?.id ? String(user.id) : null;
  const senderOwnerId =
    invoice.senderId ??
    invoice.sender_id ??
    invoice.userId ??
    invoice.user_id ??
    null;
  const receiverOwnerId =
    invoice.receiverId ??
    invoice.receiver_id ??
    invoice.customerId ??
    invoice.customer_id ??
    null;

  const viewerIsSender =
    normalizedUserId && senderOwnerId
      ? String(senderOwnerId) === normalizedUserId
      : false;
  const viewerIsReceiver =
    normalizedUserId && receiverOwnerId
      ? String(receiverOwnerId) === normalizedUserId
      : false;

  // Debug logging
  React.useEffect(() => {
    if (invoice) {
      console.log('🔍 InvoicePreview - Invoice data:', {
        invoiceNumber: invoice.invoiceNumber,
        lineItemsCount: invoice.lineItems?.length,
        lineItems: invoice.lineItems,
        subtotal: invoice.subtotal,
        tax: invoice.tax,
        discountAmount: invoice.discountAmount,
        amount: invoice.amount,
        total: invoice.total,
        calculatedTotal: calculateInvoiceTotal()
      });
    }
  }, [invoice]);

  // Add print CSS and update document title for cleaner print output
  React.useEffect(() => {
    // Store original title to restore later
    const originalTitle = document.title;
    
    // Set clean title for print (shows in browser headers if enabled)
    if (invoice?.invoiceNumber) {
      document.title = `Invoice ${invoice.invoiceNumber}`;
    }
    
    const styleId = 'invoice-print-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @page {
          margin: 0.5cm;
          size: A4;
        }
        
        @media print {
          /* Hide UI elements */
          .no-print {
            display: none !important;
          }
          
          /* Full width layout */
          .print-full-width {
            max-width: 100% !important;
            max-height: none !important;
            overflow: visible !important;
            box-shadow: none !important;
            border-radius: 0 !important;
          }
          
          /* Clean backgrounds */
          .print-no-bg {
            background: white !important;
            padding: 0 !important;
          }
          
          /* Body styling */
          body {
            background: white !important;
            margin: 0 !important;
            padding: 20px !important;
          }
          
          /* Remove browser default margins */
          html {
            margin: 0 !important;
            padding: 0 !important;
          }
        }
      `;
      document.head.appendChild(style);
    }
    
    return () => {
      // Restore original title
      document.title = originalTitle;
      
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, [invoice?.invoiceNumber]);

  const formatCurrency = (amount) => {
    const numAmount = parseFloat(amount) || 0;
    if (numAmount < 0) {
      return `(£${Math.abs(numAmount).toFixed(2)})`;
    }
    return `£${numAmount.toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '[Not set]';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const calculateLineItemTotal = (item) => {
    const subtotal = (item.quantity || 0) * (item.unitPrice || 0);
    const discount = subtotal * ((item.discount || 0) / 100);
    const afterDiscount = subtotal - discount;
    const tax = afterDiscount * ((item.taxRate || 0) / 100);
    return afterDiscount + tax;
  };

  const calculateInvoiceTotal = () => {
    if (!invoice.lineItems || !Array.isArray(invoice.lineItems)) return 0;
    return invoice.lineItems.reduce((total, item) => total + calculateLineItemTotal(item), 0);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', text: 'PENDING' },
      pending_approval: { color: 'bg-yellow-100 text-yellow-800', text: 'PENDING APPROVAL' },
      approved: { color: 'bg-green-100 text-green-800', text: 'APPROVED' },
      rejected: { color: 'bg-red-100 text-red-800', text: 'REJECTED' },
      'auto-approved': { color: 'bg-blue-100 text-blue-800', text: 'AUTO-APPROVED' },
      disputed: { color: 'bg-orange-100 text-orange-800', text: 'DISPUTED' },
      paid: { color: 'bg-green-100 text-green-800', text: 'PAID' },
      refunded_full: { color: 'bg-red-100 text-red-800', text: 'FULLY REFUNDED' },
      refunded_partial: { color: 'bg-yellow-100 text-yellow-800', text: 'PARTIALLY REFUNDED' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    );
  };

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center p-6 print-no-bg">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-5xl max-h-[95vh] overflow-hidden print-full-width">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-8 py-6 bg-gradient-to-r from-slate-50 to-gray-50 border-b border-gray-100 no-print">
          <div>
            <h3 className="text-2xl font-semibold text-gray-900 tracking-tight">{title}</h3>
            <p className="text-sm text-gray-500 mt-1">Professional invoice preview</p>
          </div>
          <div className="flex items-center space-x-3 no-print">
            {showDownloadButtons && (
              <>
                <button
                  onClick={() => onDownloadPDF && onDownloadPDF(invoice)}
                  className="inline-flex items-center px-4 py-2.5 bg-slate-700 text-white rounded-xl hover:bg-slate-800 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Download PDF
                </button>
                <button
                  onClick={() => onDownloadExcel && onDownloadExcel(invoice)}
                  className="inline-flex items-center px-4 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Download CSV
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="p-2.5 text-gray-400 hover:text-gray-600 rounded-xl hover:bg-gray-100 transition-colors duration-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Modal Content */}
        <div className="px-8 py-8 overflow-y-auto max-h-[calc(95vh-140px)]">
          {/* Invoice Header */}
          <div className="text-center mb-12">
            <div className="inline-block">
              <h1 className="text-4xl font-light text-gray-900 tracking-wide mb-3">INVOICE</h1>
              <div className="h-0.5 w-20 bg-slate-300 mx-auto mb-6"></div>
            </div>
            <div className="flex justify-center space-x-12 text-sm text-gray-500">
              <div className="text-center">
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Invoice Date</p>
                <p className="font-medium text-gray-700">{formatDate(invoice.createdAt || invoice.created_at || invoice.sentDate || invoice.receivedDate)}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Invoice Number</p>
                <p className="font-medium text-gray-700">{invoice.invoiceNumber || '[Invoice number]'}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Due Date</p>
                <p className="font-medium text-gray-700">{formatDate(invoice.dueDate)}</p>
              </div>
            </div>
          </div>

          {/* Applied Discount Banner - Shows if this invoice has a discount from previous early payment */}
          {invoice.discountAmount > 0 && parseFloat(invoice.discountAmount) > 0 && (
            <AppliedDiscountBanner 
              invoice={{
                ...invoice,
                earlyPaymentDiscountApplied: true,
                earlyPaymentOriginalInvoice: invoice.discountSourceInvoice?.invoiceNumber || 'previous invoice'
              }}
              viewType={user?.id === invoice.receiver?.id || user?.id === invoice.receiverId ? 'receiver' : 'sender'}
            />
          )}

          {/* Early Payment Banner - Shows if THIS invoice OFFERS an early payment discount */}
          {invoice.discounts && invoice.discounts.length > 0 && (
            <div className="mb-8">
              <EarlyPaymentBanner 
                invoice={invoice}
                viewType={user?.id === invoice.receiver?.id || user?.id === invoice.receiverId ? 'receiver' : 'sender'}
              />
            </div>
          )}

          {/* Company Info */}
          <div className="grid grid-cols-2 gap-8 mb-12">
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100">
              <div className="flex items-center mb-6">
                <div className="w-2 h-2 bg-slate-400 rounded-full mr-3"></div>
                <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wider">From</h3>
              </div>
              <div className="space-y-3">
                {safeSender ? (
                  <>
                    <div className="flex items-center space-x-2">
                      <p className="text-lg font-semibold text-gray-900">{safeSender.companyName || `${safeSender.firstName} ${safeSender.lastName}` || '[Company Name]'}</p>
                      <PlanBadge plan={safeSender.subscription?.plan || safeSender.subscriptionPlan} />
                    </div>
                    {(safeSender.firstName || safeSender.lastName) && <p className="text-sm text-gray-600">{safeSender.firstName} {safeSender.lastName}</p>}
                    {safeSender.email && <p className="text-sm text-slate-500">{safeSender.email}</p>}
                    {safeSender.address && <p className="text-sm text-gray-600">{safeSender.address}</p>}
                    {safeSender.city && <p className="text-sm text-gray-600">{safeSender.city}</p>}
                    {safeSender.state && <p className="text-sm text-gray-600">{safeSender.state}</p>}
                    {safeSender.zipCode && <p className="text-sm text-gray-600">{safeSender.zipCode}</p>}
                    {safeSender.country && <p className="text-sm text-gray-600">{safeSender.country}</p>}
                  </>
                ) : viewerIsSender ? (
                  <>
                    <div className="flex items-center space-x-2">
                      <p className="text-lg font-semibold text-gray-900">{user?.companyName || '[Company Name]'}</p>
                      <PlanBadge plan={user?.subscription?.plan || user?.subscriptionPlan} />
                    </div>
                    {(user?.firstName || user?.lastName) && <p className="text-sm text-gray-600">{user?.firstName} {user?.lastName}</p>}
                    {user?.email && <p className="text-sm text-slate-500">{user?.email}</p>}
                    {user?.address && <p className="text-sm text-gray-600">{user.address}</p>}
                    {user?.city && <p className="text-sm text-gray-600">{user.city}</p>}
                    {user?.state && <p className="text-sm text-gray-600">{user.state}</p>}
                    {user?.zipCode && <p className="text-sm text-gray-600">{user.zipCode}</p>}
                    {user?.country && <p className="text-sm text-gray-600">{user.country}</p>}
                  </>
                ) : (
                  <p className="text-sm text-gray-500">Sender information unavailable.</p>
                )}
              </div>
            </div>
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100">
              <div className="flex items-center mb-6">
                <div className="w-2 h-2 bg-slate-400 rounded-full mr-3"></div>
                <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wider">To</h3>
              </div>
              <div className="space-y-3">
                {safeReceiver ? (
                  <>
                    <div className="flex items-center space-x-2">
                      <p className="text-lg font-semibold text-gray-900">{safeReceiver.companyName || `${safeReceiver.firstName} ${safeReceiver.lastName}` || '[Company Name]'}</p>
                      <PlanBadge plan={safeReceiver.subscription?.plan || safeReceiver.subscriptionPlan} />
                    </div>
                    {(safeReceiver.firstName || safeReceiver.lastName) && <p className="text-sm text-gray-600">{safeReceiver.firstName} {safeReceiver.lastName}</p>}
                    {safeReceiver.email && <p className="text-sm text-slate-500">{safeReceiver.email}</p>}
                    {safeReceiver.address && <p className="text-sm text-gray-600">{safeReceiver.address}</p>}
                    {safeReceiver.city && <p className="text-sm text-gray-600">{safeReceiver.city}</p>}
                    {safeReceiver.state && <p className="text-sm text-gray-600">{safeReceiver.state}</p>}
                    {safeReceiver.zipCode && <p className="text-sm text-gray-600">{safeReceiver.zipCode}</p>}
                    {safeReceiver.country && <p className="text-sm text-gray-600">{safeReceiver.country}</p>}
                  </>
                ) : viewerIsReceiver ? (
                  <>
                    <div className="flex items-center space-x-2">
                      <p className="text-lg font-semibold text-gray-900">{user?.companyName || '[Company Name]'}</p>
                      <PlanBadge plan={user?.subscription?.plan || user?.subscriptionPlan} />
                    </div>
                    {(user?.firstName || user?.lastName) && <p className="text-sm text-gray-600">{user?.firstName} {user?.lastName}</p>}
                    {user?.email && <p className="text-sm text-slate-500">{user?.email || '[Email]'}</p>}
                    {user?.address && <p className="text-sm text-gray-600">{user.address}</p>}
                    {user?.city && <p className="text-sm text-gray-600">{user.city}</p>}
                    {user?.state && <p className="text-sm text-gray-600">{user.state}</p>}
                    {user?.zipCode && <p className="text-sm text-gray-600">{user.zipCode}</p>}
                    {user?.country && <p className="text-sm text-gray-600">{user.country}</p>}
                  </>
                ) : (
                  <p className="text-sm text-gray-500">Recipient information unavailable.</p>
                )}
              </div>
            </div>
          </div>

          {/* Invoice Details */}
          <div className="mb-8">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">Due Date:</span>
                <p className="text-gray-900">{formatDate(invoice.dueDate)}</p>
              </div>
              <div>
                <span className="font-medium text-gray-700">Status:</span>
                <p className="text-gray-900">
                  {invoice.refundStatus === 'full' 
                    ? getStatusBadge('refunded_full') 
                    : invoice.refundStatus === 'partial' 
                    ? getStatusBadge('refunded_partial')
                    : getStatusBadge(invoice.status)
                  }
                </p>
              </div>
              {invoice.poNumber && (
                <div>
                  <span className="font-medium text-gray-700">PO Number:</span>
                  <p className="text-gray-900">{invoice.poNumber}</p>
                </div>
              )}
              {(invoice.guestLookupCode || invoice.invoiceId) && (
                <div>
                  <span className="font-medium text-gray-700">
                    {invoice.guestLookupCode ? 'Lookup Code:' : 'Invoice ID:'}
                  </span>
                  <p className="text-gray-900 font-mono text-lg tracking-wider text-blue-600">
                    {invoice.guestLookupCode || invoice.invoiceId}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Line Items Table */}
          <div className="mb-12">
            <div className="flex items-center mb-6">
              <div className="w-2 h-2 bg-slate-400 rounded-full mr-3"></div>
              <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wider">Line Items</h3>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Description</th>
                    <th className="px-6 py-4 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">Qty</th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">Unit Price</th>
                    <th className="px-6 py-4 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">Tax %</th>
                    <th className="px-6 py-4 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">Discount %</th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">Total</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-100">
                  {invoice.lineItems && invoice.lineItems.length > 0 ? (
                    invoice.lineItems.map((item, index) => (
                      <tr key={index} className="hover:bg-slate-25 transition-colors duration-150">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">{item.description || '[Item description]'}</td>
                        <td className="px-6 py-4 text-sm text-center text-gray-700">{item.quantity || 0}</td>
                        <td className="px-6 py-4 text-sm text-right text-gray-700 font-medium">
                          {formatCurrency(item.unitPrice || 0)}
                        </td>
                        <td className="px-6 py-4 text-sm text-center text-gray-700">{item.taxRate || 0}%</td>
                        <td className="px-6 py-4 text-sm text-center text-gray-700">{item.discount || 0}%</td>
                        <td className="px-6 py-4 text-sm text-right text-gray-900 font-semibold">
                          {formatCurrency(calculateLineItemTotal(item))}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center">
                        <div className="text-slate-400">
                          <p className="text-sm font-medium">No line items found</p>
                          <p className="text-xs text-slate-300 mt-1">Add items to this invoice</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Invoice Summary */}
          <div className="flex justify-end mb-8">
            <div className="w-96 bg-slate-50 rounded-2xl border border-slate-200 p-8 space-y-4">
              <div className="flex justify-between items-center py-2">
                <span className="text-sm text-slate-600">Subtotal:</span>
                <span className="text-sm font-medium text-gray-700">
                  {formatCurrency(
                    invoice.subtotal || 
                    (invoice.lineItems?.reduce((sum, item) => sum + ((item.quantity || 0) * (item.unitPrice || 0)), 0) || 0)
                  )}
                </span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-sm text-slate-600">Tax:</span>
                <span className="text-sm font-medium text-gray-700">
                  {formatCurrency(
                    invoice.tax || 
                    (invoice.lineItems?.reduce((sum, item) => {
                      const subtotal = (item.quantity || 0) * (item.unitPrice || 0);
                      const discount = subtotal * ((item.discount || 0) / 100);
                      const afterDiscount = subtotal - discount;
                      return sum + (afterDiscount * ((item.taxRate || 0) / 100));
                    }, 0) || 0)
                  )}
                </span>
              </div>
              {(() => {
                const lineItemDiscountAmount = invoice.discount || 
                  (invoice.lineItems?.reduce((sum, item) => {
                    const subtotal = (item.quantity || 0) * (item.unitPrice || 0);
                    return sum + (subtotal * ((item.discount || 0) / 100));
                  }, 0) || 0);
                
                if (lineItemDiscountAmount > 0) {
                  return (
                    <div className="flex justify-between items-center py-2">
                      <span className="text-sm text-slate-600">Line Item Discounts:</span>
                      <span className="text-sm font-medium text-red-500">
                        -{formatCurrency(lineItemDiscountAmount)}
                      </span>
                    </div>
                  );
                }
                return null;
              })()}
              
              {/* Early Payment Discount - Applied from previous invoice */}
              {parseFloat(invoice.discountAmount || 0) > 0 && (
                <div className="flex justify-between items-center py-2 bg-purple-50 -mx-4 px-4 rounded-lg border border-purple-200">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-semibold text-purple-700">✨ Early Payment Discount:</span>
                    {invoice.discountSourceInvoice?.invoiceNumber && (
                      <span className="text-xs text-purple-600">
                        (from {invoice.discountSourceInvoice.invoiceNumber})
                      </span>
                    )}
                  </div>
                  <span className="text-sm font-bold text-purple-600">
                    -{formatCurrency(parseFloat(invoice.discountAmount))}
                  </span>
                </div>
              )}
              
              {/* Show additional charges if there's a difference between subtotal+tax and total */}
              {parseFloat(invoice.paymentAmount || 0) > 0 && invoice.paymentAmount !== (parseFloat(invoice.subtotal || 0) + parseFloat(invoice.tax || 0)) && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Additional Charges:</span>
                  <span className="font-medium">
                    {formatCurrency(invoice.paymentAmount - (parseFloat(invoice.subtotal || 0) + parseFloat(invoice.tax || 0)))}
                  </span>
                </div>
              )}
              <div className="border-t border-slate-300 pt-6 mt-6">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold text-gray-900">Total:</span>
                  <span className="text-2xl font-bold text-gray-900">{formatCurrency(invoice.amount || calculateInvoiceTotal())}</span>
                </div>
              </div>

              {/* Refund Information */}
              {parseFloat(invoice.refundAmount || 0) > 0 && (
                <>
                  <div className="flex justify-between items-center py-2 bg-red-50 -mx-4 px-4 rounded-lg">
                    <span className="text-sm font-semibold text-red-700">Refunded:</span>
                    <span className="text-sm font-bold text-red-600">
                      -{formatCurrency(parseFloat(invoice.refundAmount))}
                    </span>
                  </div>
                  <div className="border-t border-slate-300 pt-6 mt-2">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold text-gray-900">Net Amount:</span>
                      <span className="text-2xl font-bold text-green-700">
                        {formatCurrency((invoice.amount || calculateInvoiceTotal()) - parseFloat(invoice.refundAmount))}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Additional Information */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {invoice.notes && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Notes:</h4>
                <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">
                  {invoice.notes}
                </p>
              </div>
            )}
            {invoice.poNumber && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Purchase Order:</h4>
                <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">
                  {invoice.poNumber}
                </p>
              </div>
            )}
            {invoice.terms && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Terms & Conditions:</h4>
                <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">
                  {invoice.terms}
                </p>
              </div>
            )}
          </div>

          {/* Attachments - Only show in preview, not in print */}
          {invoice.attachments && invoice.attachments.length > 0 && (
            <div className="mb-8 no-print">
              <h4 className="font-medium text-gray-900 mb-3">
                📎 Attachments ({invoice.attachments.length})
              </h4>
              <div className="space-y-2">
                {invoice.attachments.map((attachment, index) => {
                  const handleDownloadAttachment = async () => {
                    try {
                      const response = await fetch(`/api/files/download/${encodeURIComponent(attachment.s3Key)}?filename=${encodeURIComponent(attachment.name)}`, {
                        headers: {
                          'Authorization': `Bearer ${localStorage.getItem('token')}`
                        }
                      });
                      
                      if (response.ok) {
                        const data = await response.json();
                        // Create a temporary link to trigger download with original filename
                        const link = document.createElement('a');
                        link.href = data.downloadUrl;
                        link.download = attachment.name; // Use original filename
                        link.target = '_blank';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      } else {
                        alert('Failed to download file. Please try again.');
                      }
                    } catch (error) {
                      console.error('Error downloading attachment:', error);
                      alert('Error downloading file. Please try again.');
                    }
                  };
                  
                  const formatFileSize = (bytes) => {
                    if (bytes < 1024) return bytes + ' B';
                    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
                    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
                  };
                  
                  return (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center space-x-3">
                        <FileText className="w-5 h-5 text-blue-600" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{attachment.name}</p>
                          <p className="text-xs text-gray-500">{formatFileSize(attachment.size)}</p>
                        </div>
                      </div>
                      <button
                        onClick={handleDownloadAttachment}
                        className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors duration-200 text-sm flex items-center space-x-1"
                      >
                        <Download className="w-4 h-4" />
                        <span>Download</span>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Rejection Reason - Only show in preview, not in print */}
          {invoice.rejectionReason && (
            <div className="mb-8 no-print">
              <div className="flex items-center space-x-2 mb-2">
                <h4 className="font-medium text-gray-900">Rejection Reason:</h4>
                {invoice.status === 'rejected' && (
                  <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full">
                    Rejected Invoice
                  </span>
                )}
              </div>
              <div className="bg-red-50 p-4 rounded-lg border-2 border-red-200">
                <p className="text-red-800 font-medium">
                  {invoice.rejectionReason}
                </p>
              </div>
            </div>
          )}

          {/* Refund Reason - Only show in preview, not in print */}
          {invoice.refundReason && (
            <div className="mb-8 no-print">
              <div className="flex items-center space-x-2 mb-2">
                <h4 className="font-medium text-gray-900">Refund Information:</h4>
                <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full">
                  {invoice.refundStatus === 'full' ? 'Full Refund' : 'Partial Refund'}
                </span>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border-2 border-orange-200">
                <p className="text-orange-800 font-medium mb-2">
                  <strong>Reason:</strong> {invoice.refundReason}
                </p>
                {invoice.refundDate && (
                  <p className="text-xs text-orange-600 mb-1">
                    <strong>Refund Date:</strong> {formatDate(invoice.refundDate)}
                  </p>
                )}
                {invoice.stripeRefundId && (
                  <p className="text-xs text-orange-600 font-mono">
                    <strong>Stripe Refund ID:</strong> {invoice.stripeRefundId}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Dispute Reason */}
          {invoice.disputeReason && (
            <div className="mb-8">
              <h4 className="font-medium text-gray-900 mb-2">Dispute Reason:</h4>
              <p className="text-orange-700 bg-orange-50 p-3 rounded-lg border border-orange-200">
                {invoice.disputeReason}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvoicePreview;
